import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../../ui/button';

const DropdownButton = ({ label, type, options, classes = '', top = 0, left = 0 }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState({}); // dynamically set style
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    const toggleDropdown = (event) => {
        event.stopPropagation();
        if (isOpen) {
            setIsOpen(false);
        } else {
            setIsOpen(true);
        }
    };

    const handleClickOutside = (event) => {
        if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target) &&
            buttonRef.current &&
            !buttonRef.current.contains(event.target)
        ) {
            setIsOpen(false);
        }
    };

    const adjustDropdownPosition = useCallback(() => {
        if (!dropdownRef.current || !buttonRef.current) return;

        // Get the trigger's bounding box
        const buttonRect = buttonRef.current.getBoundingClientRect();

        // Temporarily set the dropdown visible but hidden, so we can measure its height
        dropdownRef.current.style.display = 'block';
        dropdownRef.current.style.visibility = 'hidden';

        const dropdownHeight = dropdownRef.current.offsetHeight;
        const dropdownWidth = 200; // or measure .offsetWidth if your width is dynamic

        // Revert it to hidden if needed
        dropdownRef.current.style.display = '';
        dropdownRef.current.style.visibility = '';

        // Calculate space below vs. above
        const spaceBelow = window.innerHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        // Default: position below the button
        let topOffset = buttonRect.bottom + window.scrollY;
        let leftOffset = buttonRect.left + window.scrollX;

        // If there's not enough space below and there is more above, open upwards
        if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
            topOffset = buttonRect.top - dropdownHeight + window.scrollY;
        }

        // Also check if it overflows the right edge of the screen
        const viewportWidth = window.innerWidth;
        if (buttonRect.right + dropdownWidth > viewportWidth) {
            leftOffset = buttonRect.right - dropdownWidth + window.scrollX;
        }

        setDropdownStyle({
            position: 'absolute',
            top: `${topOffset + top}px`,
            left: `${leftOffset + left}px`,
            zIndex: 9999,
        });
    }, [top, left]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            adjustDropdownPosition();
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, adjustDropdownPosition]);

    const menuOptions = options.filter(option => option.label);
    const headingOption = options.find(option => option.heading);
    const footerOption = options.find(option => option.extra);

    return (
        <>
            <div className="relative inline-block" ref={buttonRef}>
                {type === 'button' ? (
                    <Button size="md" variant="primary" className={classes} onClick={toggleDropdown}>{label}</Button>
                ) : (
                    <div className={classes + 'cursor-pointer hover:bg-gray-100 hover:rounded-md'} onClick={toggleDropdown}>{label}</div>
                )}
            </div>

            {isOpen && createPortal(
                <div
                    ref={dropdownRef}
                    className="w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5"
                    style={dropdownStyle}
                >
                    {headingOption && (
                        <div className="border-b border-gray-200 px-4 py-2 text-[10px] text-gray-500 mt-2 truncate">
                            {headingOption.heading}
                        </div>
                    )}
                    <div className="py-1 flex flex-col items-center" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                        {menuOptions.map((option, index) => (
                            <div
                                key={`${option.label}-${index}`}
                                className="block cursor-pointer px-4 py-2 text-sm hover:bg-gray-100 w-full text-left flex items-center text-center"
                                role="menuitem"
                                style={{ color: option.color || 'gray' }}
                                onClick={() => {
                                    option.onClick();
                                    setIsOpen(false);
                                }}
                            >
                                {option.icon && <span className="mr-2">{option.icon}</span>}
                                {option.label}
                            </div>
                        ))}
                        {footerOption && (
                            <div className="border-t border-gray-200 px-4 py-2 text-[10px] text-gray-500 mt-2">
                                {footerOption.extra}
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default DropdownButton;
